import React, { useEffect, useState } from 'react';
import { FaPlus, FaTimes, FaTrashAlt, FaCloudUploadAlt } from 'react-icons/fa';
import TextEditor from '../../components/TextEditor';
import { uploadFile } from '../../upload-file';
import { Card, Field, Alert } from '../../ui';
import { api } from '../../ui/api';

const blankOption = () => ({ option: '', cost: '', duration: '' });
const blankRequirement = () => ({ requirement: '', details: [blankOption()] });
const filled = (v) => String(v ?? '').trim() !== '';

/**
 * The add and edit service pages share this form. `initial` is the service being
 * edited, or undefined for a new one; `onSave` receives the payload for the API.
 */
const ServiceForm = ({ initial, onSave, submitLabel }) => {
  const [categories, setCategories] = useState([]);
  const [name, setName] = useState(initial?.service_name || '');
  const [category, setCategory] = useState(initial?.category?.id || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [steps, setSteps] = useState(initial?.work_orders?.length ? initial.work_orders.map((w) => ({ step: w.step })) : [{ step: '' }]);
  const [requirements, setRequirements] = useState(
    initial?.service_requirements?.length
      ? initial.service_requirements.map((r) => ({
        requirement: r.requirement,
        details: (r.details || []).map((d) => ({ option: d.option, cost: String(d.cost ?? ''), duration: d.duration })),
      }))
      : [blankRequirement()],
  );
  const [existingImages, setExistingImages] = useState(initial?.images || []);
  const [newImages, setNewImages] = useState([]);
  const [progress, setProgress] = useState({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api('/category/all_categories')
      .then((data) => {
        setCategories(data);
        if (!category && data.length) setCategory(data[0].id);
      })
      .catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Requirement helpers --------------------------------------------------
  const updateRequirement = (ri, patch) =>
    setRequirements(requirements.map((r, i) => (i === ri ? { ...r, ...patch } : r)));
  const updateOption = (ri, oi, field, value) =>
    updateRequirement(ri, { details: requirements[ri].details.map((d, i) => (i === oi ? { ...d, [field]: value } : d)) });

  // Images -----------------------------------------------------------------
  const addImages = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length + newImages.length + existingImages.length > 10) {
      setError('A service can have at most 10 photos.');
      return;
    }
    setNewImages([...newImages, ...files.map((file) => ({ file, key: `${Date.now()}-${file.name}`, preview: URL.createObjectURL(file) }))]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!filled(name)) return setError('Give the service a name.');
    if (!category) return setError('Choose a category.');
    if (!steps.length || !steps.every((s) => filled(s.step))) return setError('Every work step needs a description.');
    if (!requirements.length || !requirements.every((r) => filled(r.requirement) && r.details.length
      && r.details.every((d) => filled(d.option) && filled(d.cost) && filled(d.duration)))) {
      return setError('Each pricing group needs a name, and every option needs a label, price and duration.');
    }
    if (!existingImages.length && !newImages.length) return setError('Add at least one photo.');

    setSaving(true);
    try {
      const uploaded = await Promise.all(newImages.map((img) =>
        uploadFile(img.file, (p) => setProgress((prev) => ({ ...prev, [img.key]: p })))));
      await onSave({
        service_name: name.trim(),
        category,
        description,
        work_orders: steps.map((s) => ({ step: s.step.trim() })),
        service_requirements: requirements.map((r) => ({
          requirement: r.requirement.trim(),
          details: r.details.map((d) => ({ option: d.option.trim(), cost: String(d.cost).trim(), duration: d.duration.trim() })),
        })),
        images: [...existingImages, ...uploaded],
      });
    } catch (err) {
      setError(err.message || 'Could not save the service.');
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-sidebar">
      <div className="stack">
        <Card title="Details">
          <div className="form">
            <div className="form-grid">
              <Field label="Service name">
                <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sofa shampooing" />
              </Field>
              <Field label="Category">
                <select className="select" value={category} onChange={(e) => setCategory(e.target.value)}>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Description" help="Shown to customers on the service page.">
              <TextEditor value={description} onChange={setDescription} />
            </Field>
          </div>
        </Card>

        <Card
          title="Pricing options"
          hint="Customers pick one option from each group when they book."
          action={<button type="button" className="btn btn-secondary btn-sm" onClick={() => setRequirements([...requirements, blankRequirement()])}><FaPlus /> Add group</button>}
        >
          <div className="stack" style={{ gap: 16 }}>
            {requirements.map((req, ri) => (
              <div key={ri} className="card" style={{ boxShadow: 'none', background: '#fafbfc' }}>
                <div className="card-body form" style={{ gap: 12 }}>
                  <div className="input-group">
                    <input
                      className="input"
                      value={req.requirement}
                      placeholder="Group name, e.g. Sofa size"
                      onChange={(e) => updateRequirement(ri, { requirement: e.target.value })}
                    />
                    <button type="button" className="icon-btn bordered" title="Remove group" disabled={requirements.length === 1}
                      onClick={() => setRequirements(requirements.filter((_, i) => i !== ri))}>
                      <FaTrashAlt />
                    </button>
                  </div>
                  <div className="option-rows">
                    <div className="option-row head">
                      <span>Option</span><span>Price (₹)</span><span>Duration</span><span />
                    </div>
                    {req.details.map((d, oi) => (
                      <div key={oi} className="option-row">
                        <input className="input" placeholder="Option" value={d.option} onChange={(e) => updateOption(ri, oi, 'option', e.target.value)} />
                        <input className="input" placeholder="Price" inputMode="numeric" value={d.cost} onChange={(e) => updateOption(ri, oi, 'cost', e.target.value)} />
                        <input className="input" placeholder="e.g. 2 hours" value={d.duration} onChange={(e) => updateOption(ri, oi, 'duration', e.target.value)} />
                        <button type="button" className="icon-btn" title="Remove option" disabled={req.details.length === 1}
                          onClick={() => updateRequirement(ri, { details: req.details.filter((_, i) => i !== oi) })}>
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => updateRequirement(ri, { details: [...req.details, blankOption()] })}>
                      <FaPlus /> Add option
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card
          title="Work steps"
          hint="The checklist field staff follow on the job."
          action={<button type="button" className="btn btn-secondary btn-sm" onClick={() => setSteps([...steps, { step: '' }])}><FaPlus /> Add step</button>}
        >
          <div className="form" style={{ gap: 10 }}>
            {steps.map((s, i) => (
              <div key={i} className="input-group" style={{ alignItems: 'center' }}>
                <span className="avatar" style={{ width: 28, height: 28, fontSize: 12 }}>{i + 1}</span>
                <input className="input" value={s.step} placeholder={`Step ${i + 1}`}
                  onChange={(e) => setSteps(steps.map((x, j) => (j === i ? { step: e.target.value } : x)))} />
                <button type="button" className="icon-btn" title="Remove step" disabled={steps.length === 1}
                  onClick={() => setSteps(steps.filter((_, j) => j !== i))}>
                  <FaTimes />
                </button>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="stack sticky-side">
        <Card title="Photos" hint="Up to 10. The first is the cover.">
          <div className="form">
            {(existingImages.length > 0 || newImages.length > 0) && (
              <div className="thumb-grid">
                {existingImages.map((url) => (
                  <div className="thumb" key={url}>
                    <img src={url} alt="" />
                    <button type="button" className="icon-btn" title="Remove" onClick={() => setExistingImages(existingImages.filter((u) => u !== url))}><FaTimes /></button>
                  </div>
                ))}
                {newImages.map((img) => (
                  <div className="thumb" key={img.key}>
                    <img src={img.preview} alt="" />
                    {progress[img.key] !== undefined && progress[img.key] < 100 && <div className="thumb-progress" style={{ width: `${progress[img.key]}%` }} />}
                    <button type="button" className="icon-btn" title="Remove" onClick={() => setNewImages(newImages.filter((x) => x.key !== img.key))}><FaTimes /></button>
                  </div>
                ))}
              </div>
            )}
            <label className="dropzone">
              <FaCloudUploadAlt />
              <span><strong>Choose photos</strong> — JPG, PNG or WebP, up to 10 MB each</span>
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={addImages} hidden />
            </label>
          </div>
        </Card>

        <Card>
          <div className="form" style={{ gap: 12 }}>
            <Alert>{error}</Alert>
            <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={saving}>
              {saving ? 'Saving…' : submitLabel}
            </button>
          </div>
        </Card>
      </div>
    </form>
  );
};

export default ServiceForm;
